import { useCallback, useEffect, useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import isNil from 'lodash/isNil'
import {
  useCustomerOptionsQuery,
  useRosterQuery,
} from '../../api/user-invitation-api-client'
import { UserListRow } from '../../api/dto/user-invitation.dto'
import { useGrantAccess } from './useGrantAccess'
import { useRemoveAccess } from './useRemoveAccess'
import { useRosterExport } from './useRosterExport'
import { useUsersFilters } from './useUsersFilters'
import { useUsersSelection } from './useUsersSelection'

/**
 * The roster page's state, assembled from the filters, the selection and the
 * bulk runs. Everything the table and its modals need comes from here, so the
 * page component stays a layout.
 */
export const useUsersTable = () => {
  const [historyUser, setHistoryUser] = useState<UserListRow | null>(null)

  const {
    page,
    setPage,
    pageSize,
    filters,
    handleStatusFilterChange,
    ...filterState
  } = useUsersFilters()

  const { data, isLoading, isFetching, isError } = useRosterQuery(
    { ...filters, page, limit: pageSize },
    // Keep the previous page/filter result visible while the next one loads so
    // rows and totals don't flash empty on a refetch.
    { placeholderData: keepPreviousData }
  )

  const { data: customerOptions = [], isPending: areCompaniesLoading } =
    useCustomerOptionsQuery()

  const records = data?.data ?? []
  const total = data?.total ?? 0
  const stats = data?.stats ?? null

  // A grant drops rows out of the default view, so the page in hand can end up
  // past the end of a shorter list, which reads as an empty table. Only acts on
  // a settled, non-empty response: the placeholder keeps the previous total
  // while the next page loads, and clamping on that would undo paging forward.
  useEffect(() => {
    if (isFetching || isNil(data) || data.total === 0) return
    const lastPage = Math.ceil(data.total / pageSize)
    if (page > lastPage) setPage(lastPage)
  }, [data, isFetching, page, pageSize, setPage])

  const {
    selectedRows,
    selectAllMatching,
    handleSelectionChange,
    handleClearSelection,
    ...selectionState
  } = useUsersSelection({ records, filters })

  const grant = useGrantAccess({
    selectedRows,
    // Closing a bulk run keeps whatever still needs attention selected, so a
    // second attempt from the bulk bar is already scoped to the failures.
    onBulkFinished: handleSelectionChange,
  })

  const remove = useRemoveAccess({
    selectedRows,
    onBulkFinished: handleSelectionChange,
  })

  const rosterExport = useRosterExport({ filters })

  const handleOpenHistory = useCallback((row: UserListRow) => {
    setHistoryUser(row)
  }, [])
  const handleCloseHistory = useCallback(() => setHistoryUser(null), [])

  /**
   * Narrows the table to the leftover access and selects it, so taking the role
   * away is the click the bulk bar already offers. Selecting reads the filters
   * from the argument rather than from state, which this render has not seen
   * yet: picking a status clears the completeness scope, and Complete alongside
   * "No company" matches nothing, so keeping it would tick no rows at all.
   */
  const handleReviewNoCompany = useCallback(() => {
    handleStatusFilterChange(['NO_COMPANIES'])
    void selectAllMatching({
      ...filters,
      completeness: undefined,
      statuses: ['NO_COMPANIES'],
    })
  }, [filters, handleStatusFilterChange, selectAllMatching])

  /**
   * Undoes that review in full, selection included: its rows are about to
   * scatter across the whole roster, where what is ticked cannot be seen.
   */
  const handleShowAllUsers = useCallback(() => {
    handleStatusFilterChange([])
    handleClearSelection()
  }, [handleClearSelection, handleStatusFilterChange])

  return {
    ...filterState,
    page,
    setPage,
    pageSize,
    handleStatusFilterChange,
    records,
    total,
    /** Per-option counts for both filters; null until the first load lands. */
    stats,
    isLoading,
    isFetching,
    isError,
    customerOptions,
    areCompaniesLoading,
    historyUser,
    handleOpenHistory,
    handleCloseHistory,
    ...selectionState,
    selectedRows,
    handleSelectionChange,
    handleClearSelection,
    handleReviewNoCompany,
    handleShowAllUsers,
    rosterExport,
    grantBuckets: grant.buckets,
    grantModalOpen: grant.modalOpen,
    grantResults: grant.results,
    grantProgress: grant.progress,
    grantAccessMutation: grant.mutation,
    handleOpenBulkGrantAccess: grant.handleOpenBulk,
    handleCloseGrantAccess: grant.handleClose,
    handleSubmitGrantAccess: grant.handleSubmit,
    handleRetryFailedGrants: grant.handleRetryFailed,
    removalPlan: remove.plan,
    removeModalOpen: remove.modalOpen,
    removeResults: remove.results,
    removeProgress: remove.progress,
    isRemovingAccess: remove.isSubmitting,
    handleOpenBulkRemoveAccess: remove.handleOpenBulk,
    handleCloseRemoveAccess: remove.handleClose,
    handleSubmitRemoveAccess: remove.handleSubmit,
  }
}
